/**
 * WhatsApp message templates for practical driving lessons.
 *
 * The message layout is deliberately identical to the format the
 * autoescuela signed off — do not tweak spacing, emojis or wording
 * without checking with the customer first.
 */

function formatDateEs(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  } catch (_) {
    return iso;
  }
}

/**
 * Build the personalised WhatsApp message for one participant of a
 * practical (potentially shared) lesson.
 *
 * @param {Object} params
 * @param {Object} params.viewer            The participant reading the message.
 *   { name, is_main, pickup_location, pickup_time }
 * @param {Array}  params.participants      All participants sorted by pickup_time.
 *   Each: { name, is_main, pickup_location, pickup_time, student_id }
 * @param {Object} params.appointment       Raw appointment (date, teacher, ...).
 * @param {Object|null} params.vehicle      { plate } — line omitted if null.
 * @param {boolean} params.hideIdentity     If true, other participants stay anonymous.
 * @param {string} params.franchise         Signature / brand line.
 * @returns {string}
 */
export function buildLessonMessage({
  viewer,
  participants = [],
  appointment,
  vehicle = null,
  hideIdentity = false,
  franchise = "Natur Autoescuela",
}) {
  const lines = [];
  lines.push(`Hola ${viewer.name.split(" ")[0]}.`);
  lines.push("");
  lines.push(`Tienes una práctica programada el ${formatDateEs(appointment.date)}.`);
  lines.push("");
  if (appointment.teacher) lines.push(`Profesor: ${appointment.teacher}`);
  if (vehicle && vehicle.plate) lines.push(`Vehículo: ${vehicle.plate}`);
  lines.push("");
  lines.push("Tu recogida:");
  if (viewer.pickup_location) lines.push(`📍 ${viewer.pickup_location}`);
  if (viewer.pickup_time) lines.push(`⏰ ${viewer.pickup_time}`);

  // If this is not a shared session at all, jump straight to the signature.
  const others = participants.filter((p) => p.student_id !== viewer.student_id);
  if (others.length === 0) {
    lines.push("");
    lines.push(franchise);
    return lines.join("\n").replace(/\n{3,}/g, "\n\n");
  }

  lines.push("");
  if (hideIdentity) {
    lines.push("Esta práctica se realizará en modalidad compartida con otros alumnos.");
  } else {
    const before = others.filter((p) => (p.pickup_time || "") < (viewer.pickup_time || ""));
    const after = others.filter((p) => (p.pickup_time || "") >= (viewer.pickup_time || ""));

    if (viewer.is_main && before.length === 0) {
      lines.push("La práctica se realizará en modalidad compartida.");
    } else {
      lines.push("Esta práctica forma parte de una sesión compartida.");
    }

    if (before.length > 0) {
      lines.push("");
      lines.push(before.length === 1
        ? "La sesión comienza previamente con:"
        : "La sesión comienza previamente con:");
      for (const p of before) {
        lines.push(`• ${p.name}`);
        if (p.pickup_location) lines.push(`📍 ${p.pickup_location}`);
        if (p.pickup_time) lines.push(`⏰ ${p.pickup_time}`);
      }
    }
    if (after.length > 0) {
      lines.push("");
      lines.push(after.length === 1
        ? "Alumno incorporado posteriormente:"
        : "Alumnos incorporados posteriormente:");
      for (const p of after) {
        lines.push(`• ${p.name}`);
        if (p.pickup_location) lines.push(`📍 ${p.pickup_location}`);
        if (p.pickup_time) lines.push(`⏰ ${p.pickup_time}`);
      }
    }
  }

  lines.push("");
  lines.push(franchise);
  return lines.join("\n").replace(/\n{3,}/g, "\n\n");
}

/**
 * Collect all participants (main + shared) of an appointment into a single
 * time-sorted array, ready to be passed into `buildLessonMessage`.
 */
export function participantsOf(appointment) {
  const main = {
    student_id: appointment.student?.id || appointment.student_id,
    name: appointment.student?.name || "Alumno",
    phone: appointment.student?.phone || "",
    pickup_location: appointment.pickup_location || "",
    pickup_time: appointment.time || "",
    is_main: true,
  };
  const shared = (appointment.shared_students || []).map((s) => ({
    student_id: s.id,
    name: s.name,
    phone: s.phone || "",
    pickup_location: s.pickup_location || "",
    pickup_time: s.pickup_time || "",
    is_main: false,
  }));
  return [main, ...shared].sort((a, b) =>
    (a.pickup_time || "").localeCompare(b.pickup_time || "")
  );
}
